import { Box, Link, Typography, Divider, styled, Stack } from "fashion/fabrics";
import BlogHeader from "nodes/blog/BlogHeader";
import { POST_INDEX } from "names/blog";
import CardButton from "nodes/core/CardButton";

const SectionStack = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
}));
const BlogPostStack = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(3),
}));

const CARD_ID_COMMON_DECK = "8ad3212f-cc39-40cd-978e-1bb45f418a7f";
const CARD_ID_YOSEMITE = "dbef24f0-bb5f-4bfc-a436-bfe82cc33eac";
const LINK_COMMON_DECK = "https://onecommon.cards/deck";
const LINK_CONCEPT_MEMOIR = "https://concept.memoir.cards";
const LINK_EXAMPLE_CARD =
  "https://memoir.cards/~/dbef24f0-bb5f-4bfc-a436-bfe82cc33eac";
const LINK_VIDEO_EMBED =
  "https://www.youtube.com/embed/_WgvCDzUYk4?si=CBXR8fjlZlPrv1OO";

const VideoIframe = styled("iframe")({
  width: "100%",
  border: "none",
  aspectRatio: "16 / 9", // Maintain 16:9 aspect ratio
});

const BlogPostCommunitiesTemplateDeck = () => (
  <BlogPostStack>
    <BlogHeader postIndex={POST_INDEX.BLOG_COMMUNITIES_TEMPLATE_DECK} />

    <SectionStack>
      <Typography>
        We are excited to kick off a <b>collaborative template project</b> idea
        to build a unique deck of 52 playing cards, each featuring an image and
        link that can be added to the card once copied. All centered around a
        chosen theme to be re-posted on other communities and platforms.
      </Typography>

      <Typography component="ul">
        <li>
          Link to the <b>52 cards template:</b>{" "}
          <Link href={LINK_COMMON_DECK}>onecommon.cards/deck</Link>
        </li>
        <li>
          Card Memoir concept web-app:{" "}
          <Link href={LINK_CONCEPT_MEMOIR}>concept.memoir.cards</Link>
        </li>
        <li>Let's get creative and build an awesome community card deck!</li>
      </Typography>
    </SectionStack>
    <Divider variant="middle" />

    <SectionStack>
      <Box display="flex" justifyContent="center">
        <CardButton cardId={CARD_ID_COMMON_DECK} />
      </Box>
      <Typography variant="h4">Here's the concept:</Typography>
      <Typography>
        We've created <Link href={LINK_COMMON_DECK}>52 template cards</Link>,
        each representing a playing card (Ace through King, across four suits).
        Your mission, should you choose to accept it, is to claim a card and
        contribute:
      </Typography>

      <Typography component="ul">
        <li>
          <b>A generated image</b>: This could be a photograph, digital art, or
          any other visually appealing image related to the theme.
        </li>
        <li>
          <b>A relevant web link</b>: This link could point to a website,
          article, video, or any other online resource that expands on the
          image.
        </li>
      </Typography>

      <Typography>
        Once all 52 cards are filled, we'll create a digital version of the deck
        accessible to everyone. Plus, we can offer a print-on-demand option for
        those who want a physical copy (ask us to learn how)! Send us a message
        to express your interest in printed cards once the deck is complete.
      </Typography>
    </SectionStack>
    <Divider variant="middle" />

    <SectionStack>
      <Typography variant="h4">How to participate:</Typography>
      <Typography component="ol">
        <li>
          Comment below with the card you want to claim (e.g., "King of
          Diamond").
        </li>
        <li>Watch the video to learn how to copy and edit a card:</li>
        <VideoIframe
          src={LINK_VIDEO_EMBED}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
        <li>
          Edit in graphic mode drag and <b>upload your image</b> in the center
          tiles
        </li>
        <li>
          Edit the bottom left tile and add a <b>web-link & notes</b> to expand
          on the image
        </li>
        <li>Publish the card and share your link on a comment</li>
      </Typography>
    </SectionStack>

    <Divider variant="middle" />

    <SectionStack>
      <Typography variant="h4">Example Theme: National Parks:</Typography>
      <Typography component="ul">
        <li>
          <b>Card</b>: King of Diamond
        </li>
        <li>
          <b>Image</b>: A breathtaking photo of Yosemite Valley.
        </li>
        <li>
          <b>Link</b>: A link to the official Yosemite National Park website.
        </li>
        <li>
          <b>Card</b>:{" "}
          <Link href={LINK_EXAMPLE_CARD}>
            memoir.cards/~/dbef24f0-bb5f-4bfc-a436-bfe82cc33eac
          </Link>
        </li>
      </Typography>
      <Box display="flex" justifyContent="center">
        <CardButton cardId={CARD_ID_YOSEMITE} />
      </Box>
    </SectionStack>
  </BlogPostStack>
);

export default BlogPostCommunitiesTemplateDeck;
